import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import {
  Dimensions,
  I18nManager,
  LayoutChangeEvent,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Text } from '@components/base'
import type { SliderMarkerProps, SliderProps, SliderStepLabel } from './Slider'

const MARKER_SIZE = 24
const INITIAL_WIDTH = Dimensions.get('screen').width - MARKER_SIZE * 2
const DEFAULT_HIT_SLOP = 16

const clamp = (value: number, min: number, max: number) => {
  if (value < min) {
    return min
  }

  if (value > max) {
    return max
  }

  return value
}

const getStepPrecision = (step?: number) => {
  if (!step || Number.isInteger(step)) {
    return 0
  }

  return step.toString().split('.')[1]?.length ?? 0
}

const buildStepValues = (min: number, max: number, step?: number) => {
  if (!step || step <= 0 || min === max) {
    return [min, max]
  }

  const precision = getStepPrecision(step)
  const steps: number[] = []
  const count = Math.max(1, Math.round((max - min) / step))

  for (let index = 0; index <= count; index += 1) {
    const value = min + index * step
    steps.push(Number(value.toFixed(precision)))
  }

  const lastValue = steps[steps.length - 1]
  if (lastValue !== max) {
    steps[steps.length - 1] = max
  }

  return steps
}

const getClosestStepIndex = (stepValues: number[], value: number) => {
  let closestIndex = 0
  let closestDistance = Number.POSITIVE_INFINITY

  stepValues.forEach((stepValue, index) => {
    const distance = Math.abs(stepValue - value)
    if (distance < closestDistance) {
      closestDistance = distance
      closestIndex = index
    }
  })

  return closestIndex
}

const getPositionFromIndex = (index: number, length: number, sliderLength: number) => {
  if (length <= 1 || sliderLength <= 0) {
    return 0
  }

  return (sliderLength * index) / (length - 1)
}

const removeVerticalOffset = (style?: ViewStyle) => {
  return {
    ...style,
    marginTop: undefined,
  }
}

type StepLabelValue = NonNullable<SliderProps['stepsAs']>[number]

const isStructuredStepLabel = (step: StepLabelValue): step is SliderStepLabel => {
  return typeof step === 'object' && 'index' in step
}

const DefaultMarker = ({
  disabled,
  disabledMarkerStyle,
  markerStyle,
  pressed,
  pressedMarkerStyle,
}: {
  disabled: boolean
  disabledMarkerStyle?: ViewStyle
  markerStyle: ViewStyle
  pressed: boolean
  pressedMarkerStyle?: ViewStyle
}) => {
  return (
    <View style={[markerStyle, pressed && pressedMarkerStyle, disabled && disabledMarkerStyle]} />
  )
}

export const SingleValueSlider = ({
  containerStyle,
  customMarker,
  disabledMarkerStyle,
  enabledOne = true,
  markerStyle,
  max = 10,
  min = 0,
  onValuesChange,
  onValuesChangeFinish,
  onValuesChangeStart,
  pressedMarkerStyle,
  progressColor = {},
  selectedStyle,
  showStepLabels,
  showStepMarkers,
  showSteps,
  sliderLength: sliderLengthProp,
  step,
  stepLabelStyle,
  stepMarkerStyle,
  stepsAs,
  testID,
  trackColor = {},
  trackStyle,
  unselectedStyle,
  values = [],
  wrapperStyle,
}: SliderProps) => {
  const themedStyles = useStyleSheet(themeStyle)
  const [measuredSliderLength, setMeasuredSliderLength] = useState(INITIAL_WIDTH)
  const [pressed, setPressed] = useState(false)
  const [currentValue, setCurrentValue] = useState(values[0] ?? min)
  const stepValues = useMemo(() => buildStepValues(min, max, step), [min, max, step])
  const isEnabled = enabledOne !== false
  const sliderLength = sliderLengthProp ?? measuredSliderLength
  const currentValueRef = useRef(currentValue)
  const gestureStartPositionRef = useRef(0)
  const stepLabels = useMemo(() => {
    if (!stepsAs?.length) {
      return null
    }

    const labelsByIndex = new Map<number, string>()

    stepsAs.forEach((stepLabel, index) => {
      if (isStructuredStepLabel(stepLabel)) {
        labelsByIndex.set(
          stepLabel.index,
          `${stepLabel.prefix}${stepLabel.stepLabel}${stepLabel.suffix}`,
        )
        return
      }

      labelsByIndex.set(index, String(stepLabel))
    })

    return labelsByIndex
  }, [stepsAs])

  const flattenedTrackStyle = StyleSheet.flatten<ViewStyle>([themedStyles.trackStyle, trackStyle])
  const flattenedMarkerStyle = StyleSheet.flatten<ViewStyle>([themedStyles.marker, markerStyle])
  const renderedMarkerStyle = removeVerticalOffset(flattenedMarkerStyle)
  const flattenedPressedMarkerStyle = StyleSheet.flatten<ViewStyle>(pressedMarkerStyle)
  const renderedPressedMarkerStyle = removeVerticalOffset(flattenedPressedMarkerStyle)
  const flattenedDisabledMarkerStyle = StyleSheet.flatten<ViewStyle>([
    themedStyles.disabledMarker,
    disabledMarkerStyle,
  ])
  const flattenedSelectedStyle = StyleSheet.flatten<ViewStyle>([
    themedStyles.selectedTrackStyle,
    progressColor,
    selectedStyle,
  ])
  const flattenedUnselectedStyle = StyleSheet.flatten<ViewStyle>([
    themedStyles.unselectedTrackStyle,
    trackColor,
    unselectedStyle,
  ])
  const markerWidth =
    typeof flattenedMarkerStyle.width === 'number' ? flattenedMarkerStyle.width : MARKER_SIZE
  const markerHeight =
    typeof flattenedMarkerStyle.height === 'number' ? flattenedMarkerStyle.height : MARKER_SIZE
  const markerOffsetY =
    typeof flattenedMarkerStyle.marginTop === 'number' ? flattenedMarkerStyle.marginTop : 0
  const trackHeight =
    typeof flattenedTrackStyle.height === 'number' ? flattenedTrackStyle.height : 4
  const trackTop = Math.max(0, markerOffsetY) + (markerHeight - trackHeight) / 2
  const sliderHeight = Math.max(markerHeight + Math.max(0, markerOffsetY), trackTop + trackHeight)
  const currentIndex = getClosestStepIndex(stepValues, currentValue)
  const currentPosition = getPositionFromIndex(currentIndex, stepValues.length, sliderLength)
  const showMarkers = showSteps || showStepMarkers
  const externalValue = values[0] ?? min

  useEffect(() => {
    currentValueRef.current = externalValue
    setCurrentValue(externalValue)
  }, [externalValue])

  const onLayout = (event: LayoutChangeEvent) => {
    if (sliderLengthProp) {
      return
    }

    setMeasuredSliderLength(event.nativeEvent.layout.width)
  }

  const updateValueFromPosition = (position: number) => {
    const boundedPosition = clamp(position, 0, sliderLength)
    const rawIndex =
      stepValues.length <= 1 || sliderLength <= 0
        ? 0
        : Math.round((boundedPosition / sliderLength) * (stepValues.length - 1))
    const nextIndex = clamp(rawIndex, 0, stepValues.length - 1)
    const nextValue = stepValues[nextIndex]

    if (nextValue !== currentValueRef.current) {
      currentValueRef.current = nextValue
      setCurrentValue(nextValue)
      onValuesChange?.([nextValue])
    } else {
      setCurrentValue(nextValue)
    }
  }

  const panGesture = Gesture.Pan()
    .enabled(isEnabled)
    .runOnJS(true)
    .hitSlop(DEFAULT_HIT_SLOP)
    .onBegin(() => {
      gestureStartPositionRef.current = currentPosition
      setPressed(true)
      onValuesChangeStart?.()
    })
    .onUpdate((event) => {
      const translationX = I18nManager.isRTL ? -event.translationX : event.translationX
      updateValueFromPosition(gestureStartPositionRef.current + translationX)
    })
    .onFinalize(() => {
      setPressed(false)
      onValuesChangeFinish?.([currentValueRef.current])
    })

  const tapGesture = Gesture.Tap()
    .enabled(isEnabled)
    .runOnJS(true)
    .onEnd((event) => {
      onValuesChangeStart?.()
      updateValueFromPosition(I18nManager.isRTL ? sliderLength - event.x : event.x)
      onValuesChangeFinish?.([currentValueRef.current])
    })

  const markerProps: SliderMarkerProps = {
    currentValue,
    disabledMarkerStyle: flattenedDisabledMarkerStyle,
    enabled: isEnabled,
    markerStyle: renderedMarkerStyle,
    pressed,
    pressedMarkerStyle: renderedPressedMarkerStyle,
  }

  const renderedMarker = customMarker ? (
    customMarker(markerProps)
  ) : (
    <DefaultMarker
      disabled={!isEnabled}
      disabledMarkerStyle={flattenedDisabledMarkerStyle}
      markerStyle={renderedMarkerStyle}
      pressed={pressed}
      pressedMarkerStyle={renderedPressedMarkerStyle}
    />
  )

  return (
    <View testID={testID} onLayout={onLayout} style={wrapperStyle}>
      <View
        style={[
          themedStyles.containerStyle,
          containerStyle,
          { width: sliderLength, height: sliderHeight },
        ]}
      >
        <GestureDetector gesture={tapGesture}>
          <View style={[themedStyles.fullTrack, { width: sliderLength, height: sliderHeight }]}>
            <View
              style={[
                themedStyles.trackBase,
                flattenedUnselectedStyle,
                flattenedTrackStyle,
                {
                  top: trackTop,
                  width: sliderLength,
                  height: trackHeight,
                },
              ]}
            />
            <View
              style={[
                themedStyles.trackFill,
                flattenedSelectedStyle,
                flattenedTrackStyle,
                {
                  top: trackTop,
                  width: currentPosition,
                  height: trackHeight,
                },
              ]}
            />
            {showMarkers && stepValues.length > 1 && (
              <View
                pointerEvents="none"
                style={[
                  themedStyles.stepMarkersContainer,
                  {
                    top: trackTop,
                    width: sliderLength,
                    height: trackHeight,
                  },
                ]}
              >
                {stepValues.map((stepValue, index) => (
                  <View
                    key={`${stepValue}-${index}`}
                    style={[themedStyles.stepMarker, stepMarkerStyle]}
                  />
                ))}
              </View>
            )}
          </View>
        </GestureDetector>
        <View
          style={[
            themedStyles.markerContainer,
            {
              left: currentPosition - markerWidth / 2,
              top: Math.max(0, markerOffsetY),
              width: markerWidth,
              height: markerHeight,
            },
          ]}
        >
          <GestureDetector gesture={panGesture}>
            <View style={themedStyles.touch}>{renderedMarker}</View>
          </GestureDetector>
        </View>
      </View>
      {showStepLabels && stepValues.length > 1 && (
        <View style={[themedStyles.stepLabelsContainer, { width: sliderLength }]}>
          {stepValues.map((stepValue, index) => (
            <Text key={`${stepValue}-${index}`} type="small" style={stepLabelStyle}>
              {stepLabels?.get(index) ?? String(stepValue)}
            </Text>
          ))}
        </View>
      )}
    </View>
  )
}

const themeStyle = StyleService.create({
  containerStyle: {
    height: 24,
  },
  fullTrack: {
    justifyContent: 'center',
  },
  trackBase: {
    position: 'absolute',
    left: 0,
    borderRadius: 2,
  },
  trackFill: {
    position: 'absolute',
    left: 0,
    borderRadius: 2,
  },
  selectedTrackStyle: {
    backgroundColor: 'theme.primary.base',
  },
  unselectedTrackStyle: {
    backgroundColor: 'theme.surface.base1',
  },
  marker: {
    backgroundColor: 'theme.solid.white',
    width: MARKER_SIZE,
    height: MARKER_SIZE,
    borderRadius: MARKER_SIZE / 2,
    borderWidth: 1,
    borderColor: 'theme.primary.base',
  },
  disabledMarker: {
    opacity: 0.5,
  },
  markerContainer: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
  },
  trackStyle: {
    height: 4,
    borderRadius: 2,
  },
  stepMarkersContainer: {
    position: 'absolute',
    left: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  stepMarker: {
    backgroundColor: 'theme.background',
    width: 2,
    height: 4,
    borderRadius: 0,
  },
  stepLabelsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  touch: {
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
    backgroundColor: 'theme.transparent',
  },
})
